import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output, TemplateRef, ViewChild } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { ProjectsService } from '../../../../services/api/projects.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { HttpParams } from '@angular/common/http';

const params = new HttpParams().set('size', '9999');

interface ProjectDocument {
 projectDocumentId: number
  description: string
  attachments: Attachment[]
}

export interface Attachment {
  attachmentId: number
  fileNameOriginal: string
  url: string
}

@Component({
  selector: 'projects-documents',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent
],
  templateUrl: './projects-documents.component.html',
  styleUrl: './projects-documents.component.scss'
})
export class ProjectsDocumentsComponent implements OnInit, OnChanges {
  @ViewChild('editModalContent') private editModalContent!: TemplateRef<unknown>;

  @Input() projectId?: string;
  @Output() onProjectDocumentUpdated = new EventEmitter<ProjectDocument[]>();

  private editModalRef?: NgbModalRef;

  documentsForm: FormGroup;
  documents: ProjectDocument[] = [];
  selectedDocumentId: number | null = null;
  selectedFile: File | null = null;
  isEditing = false;
  editingDocumentId: number | null = null;
  editForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private confirmationDialogService: ConfirmationDialogService,
    private modalService: NgbModal
  ) {
    this.documentsForm = this.fb.group({
      description: ['', Validators.required]
    });
    
    this.editForm = this.fb.group({
      description: ['', Validators.required]
    });
    
    this.documents = [];
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange && changes['projectId'].currentValue) {
      this.loadDocuments();
    }
  }

  private loadDocuments(): void {
    if (!this.projectId) {
      console.log('loadDocuments called but projectId is not set');
      return;
    }

    this.projectsService.listProjectDocuments(Number(this.projectId), { params })
      .subscribe({
        next: (response) => {
          this.documents = response.data.projectDocuments;
          this.onProjectDocumentUpdated.emit(this.documents);
        },
        error: (error) => {
          this.alertNotificationService.error('Error loading project documents');
          console.error('Error:', error);
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length) {
      this.selectedFile = input.files[0];
      if (!this.documentsForm.get('description')?.value) {
        this.documentsForm.patchValue({
          description: this.selectedFile.name.split('.')[0]
        });
      }
    }
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    const fileInput = document.getElementById('documentFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit(): void {
    if (this.documentsForm.valid && this.selectedFile && this.projectId) {
      const data = {
        description: this.documentsForm.value.description
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      formData.append('file', this.selectedFile, this.selectedFile.name);

      this.projectsService.createProjectDocument(Number(this.projectId), formData)
        .subscribe({
          next: (response) => {
            this.alertNotificationService.success('Document added successfully');
            this.loadDocuments(); 
            this.resetForm();
          },
          error: (error) => {
            this.alertNotificationService.error('Error adding document');
            console.error('Error:', error);
          }
        });
    }
  }

  resetForm(): void {
    this.documentsForm.reset();
    this.selectedFile = null;
    const fileInput = document.getElementById('documentFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  downloadFile(document: ProjectDocument): void {
    console.log(document);
    
    if (document.attachments[0].url) {
      this.projectsService.GET(document.attachments[0].url).subscribe({
        next: (response:any) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }, 
        error: (error:any) => {
          this.alertNotificationService.error('Error downloading file');
          console.error('Error:', error);
        }
      })
    }
  }

  openDeleteConfirmation(documentId: number): void {
    this.selectedDocumentId = documentId;
    const message = 'Are you sure you want to delete this document?';
    
    this.confirmationDialogService.open(
      message,
      () => {
        if (this.selectedDocumentId && this.projectId) {
          this.projectsService.deleteProjectDocument(Number(this.projectId), this.selectedDocumentId)
            .subscribe({
              next: () => {
                this.alertNotificationService.success('Document deleted successfully');
                this.loadDocuments();
                this.selectedDocumentId = null;
              },
              error: (error) => {
                this.alertNotificationService.error('Error deleting document');
                console.error('Error:', error);
                this.selectedDocumentId = null;
              }
            });
        }
      },
      () => { this.selectedDocumentId = null; }
    );
  }

  getDescription(): FormControl { return this.getDocumentsFormControl('description'); }

  getDocumentsFormControl(name: string): FormControl { return this.documentsForm.get(name) as FormControl; }

  getEditFormControl(name: string): FormControl { return this.editForm.get(name) as FormControl; }

  startEditing(document: ProjectDocument): void {
    this.isEditing = true;
    this.editingDocumentId = document.projectDocumentId;
    this.editForm.patchValue({
      description: document.description
    });

    this.editModalRef = this.modalService.open(this.editModalContent, {
      centered: true,
      backdrop: 'static'
    });

    // Chiusura con ESC: equivale a "Cancel".
    this.editModalRef.result.catch(() => {
      this.editModalRef = undefined;
      if (this.isEditing) {
        this.cancelEditing();
      }
    });
  }

  cancelEditing(): void {
    this.isEditing = false;
    this.editingDocumentId = null;
    this.editForm.reset();

    const ref = this.editModalRef;
    this.editModalRef = undefined;
    ref?.dismiss();
  }

  saveEdit(): void {
    if (this.editForm.valid && this.editingDocumentId && this.projectId) {
      const data = {
        description: this.editForm.value.description
      };

      this.projectsService.updateProjectDocument(Number(this.projectId), this.editingDocumentId, data)
        .subscribe({
          next: () => {
            this.alertNotificationService.success('Document description updated successfully');
            this.loadDocuments();
            this.cancelEditing();
          },
          error: (error) => {
            this.alertNotificationService.error('Error updating document description');
            console.error('Error:', error);
          }
        });
    }
  }
}